// ./src/utils/api/TagService.ts
//
// Tag service — talks to the eventsmagic-api `tag` entity.
//
// A tag is an account-level label: `name` + `color` (+ status). It is attachable
// to people (contacts) and, through them, surfaced across event pages. The API
// filter supports id + searchKeyword (case-insensitive partial match on name).
//
// Everything returns an OpResult directly (no wrapper) — read errors at the
// render boundary with the opResultHelpers.
import { gql } from "@apollo/client";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";
import { runBulkWrites } from "../bulkWrites";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const TAG_FIELDS = `
  id
  accountId
  name
  color
  status
  rights
  createdAt
  updatedAt
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const TAG_LIST_QUERY = gql`
  query TagList($filter: TagFilter, $params: PaginationAndSorting) {
    tagList(filter: $filter, params: $params) {
      code
      data {
        ${TAG_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TAG_GET_QUERY = gql`
  query TagGet($id: ID) {
    tagGet(id: $id) {
      code
      data {
        ${TAG_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TAG_GET_MANY_QUERY = gql`
  query TagGetMany($ids: [ID]) {
    tagGetMany(ids: $ids) {
      code
      data {
        ${TAG_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const TAG_CREATE_MUTATION = gql`
  mutation TagCreate($params: TagInput) {
    tagCreate(params: $params) {
      code
      data {
        ${TAG_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TAG_UPDATE_MUTATION = gql`
  mutation TagUpdate($where: TagWhereInput, $params: TagInput) {
    tagUpdate(where: $where, params: $params) {
      code
      data {
        ${TAG_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TAG_REMOVE_MUTATION = gql`
  mutation TagRemove($where: TagWhereInput) {
    tagRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api tag schema)
// =============================================================================

export interface Tag {
  id?: string | undefined;
  accountId?: string | undefined;
  name?: string | undefined;
  color?: string | undefined;
  status?: number | undefined;
  /** Actions the current caller may perform on this record ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
}

export interface TagInput {
  name?: string | null | undefined;
  color?: string | null | undefined;
}

export interface TagFilter {
  id?: string[] | null | undefined;
  /** Case-insensitive partial match on the tag name. */
  searchKeyword?: string | null | undefined;
}

export interface TagWhereInput {
  id?: string | undefined;
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * Tag service — CRUD over the account's tag catalog.
 * list / get / getMany / create / update / remove. There is no server-side
 * bulk remove; removeMany fans out individual removes for the caller's
 * convenience.
 */
export class TagService {
  /** List tags with optional filtering, pagination and sorting. */
  static async list(
    filter?: TagFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(TAG_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
  }

  /** Get a single tag by ID. */
  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(TAG_GET_QUERY, { id });
  }

  /** Get multiple tags by IDs. */
  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(TAG_GET_MANY_QUERY, { ids });
  }

  /** Create a tag. */
  static async create(params: TagInput): Promise<OpResult> {
    return ApiService.executeMutation(TAG_CREATE_MUTATION, { params });
  }

  /** Update a tag. */
  static async update(
    where: TagWhereInput,
    params: TagInput
  ): Promise<OpResult> {
    return ApiService.executeMutation(TAG_UPDATE_MUTATION, { where, params });
  }

  /** Remove a tag (soft delete). */
  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(TAG_REMOVE_MUTATION, { where: { id } });
  }

  /**
   * Remove several tags. The API has no bulk-remove mutation, so this fans out
   * individual removes and returns the first failure (or the last success).
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) return new OpResult();
    const results = await runBulkWrites(ids, (id) => TagService.remove(id));
    const failed = results.find((r) => r.code !== OP_RESULT_CODES.OK);
    return failed ?? results[results.length - 1] ?? new OpResult();
  }
}
